import { FC, Fragment, useContext } from 'react';
import QRCode from 'react-qr-code';

import currentStepRingInner from '@/assets/images/questionnaire/current-step-ring-inner.png';
import currentStepRingOuter from '@/assets/images/questionnaire/current-step-ring-outer.png';
import LogoMark from '@/assets/svg/logos/logo-mark.svg';
import CheckIcon from '@/assets/svg/questionnaire/check.svg';
import ConnectorDone from '@/assets/svg/questionnaire/connector-done.svg';
import ConnectorPending from '@/assets/svg/questionnaire/connector-pending.svg';
import { PagePaths } from '@/routes/page-paths';
import { theme, Typography } from '@/shared/design-system';

import { JourneyState, QuestionnaireJourneyContext } from '../questionnaire-journey-context';
import {
  JourneyItemStatus,
  StyledCircle,
  StyledCircleSlot,
  StyledConnector,
  StyledDots,
  StyledEntries,
  StyledEntry,
  StyledInnerRing,
  StyledOuterRing,
  StyledQrCard,
  StyledRail,
  StyledRailContent,
  StyledRailLogo,
  StyledTimeline,
} from './journey-rail.styled';

const QR_SIZE = 80;
const LOGO_SIZE = 32;

const ITEMS = [
  { title: 'Create account', subtitle: { done: 'Done' } },
  { title: 'Investor profile', subtitle: { current: 'In progress', done: 'Done' } },
  {
    title: 'Verify your identity',
    subtitle: { current: 'Next (about 5 minutes)', pending: 'Next (about 5 minutes)' },
  },
  { title: 'Start investing', subtitle: { pending: 'Unlocks after verification' } },
] as const;

const STATUSES: Record<JourneyState, JourneyItemStatus[]> = {
  profileInProgress: ['done', 'current', 'pending', 'pending'],
  profileDone: ['done', 'done', 'current', 'pending'],
};

const subtitleColor = (status: JourneyItemStatus) =>
  status === 'current' ? theme.colors.text.brand : theme.colors.text.tertiary;

export const JourneyRail: FC = () => {
  const { state } = useContext(QuestionnaireJourneyContext);
  const statuses = STATUSES[state];
  const getAppUrl = `${window.location.origin}${PagePaths.getApp}`;

  return (
    <StyledRail>
      <StyledRailLogo>
        <LogoMark width={LOGO_SIZE} height={LOGO_SIZE} aria-label="Gamma Prime" role="img" />
      </StyledRailLogo>

      <StyledRailContent>
        <Typography as="h2" variant="h3" fontWeight={600}>
          <Typography as="span" variant="h3" fontWeight={600} color={theme.colors.text.tertiary}>
            Almost ready
          </Typography>
          <br />
          to invest
        </Typography>

        <StyledTimeline>
          <StyledDots aria-hidden>
            {statuses.map((status, index) => (
              <Fragment key={ITEMS[index].title}>
                {index > 0 && (
                  <StyledConnector>
                    {statuses[index - 1] === 'done' ? <ConnectorDone /> : <ConnectorPending />}
                  </StyledConnector>
                )}
                <StyledCircleSlot>
                  {status === 'current' && (
                    <>
                      <StyledOuterRing src={currentStepRingOuter} alt="" />
                      <StyledInnerRing src={currentStepRingInner} alt="" />
                    </>
                  )}
                  <StyledCircle $status={status}>
                    {status === 'done' ? <CheckIcon /> : index + 1}
                  </StyledCircle>
                </StyledCircleSlot>
              </Fragment>
            ))}
          </StyledDots>

          <StyledEntries>
            {ITEMS.map((item, index) => {
              const status = statuses[index];
              const subtitles: Partial<Record<JourneyItemStatus, string>> = item.subtitle;

              return (
                <StyledEntry
                  key={item.title}
                  aria-current={status === 'current' ? 'step' : undefined}
                >
                  <Typography as="span" variant="body2" fontWeight={500}>
                    {item.title}
                  </Typography>
                  <Typography as="span" variant="body3" color={subtitleColor(status)}>
                    {subtitles[status]}
                  </Typography>
                </StyledEntry>
              );
            })}
          </StyledEntries>
        </StyledTimeline>
      </StyledRailContent>

      <StyledQrCard>
        <QRCode
          value={getAppUrl}
          size={QR_SIZE}
          bgColor={theme.colors.background.canvas}
          fgColor={theme.colors.text.primary}
          role="img"
          aria-label="QR code to continue on your phone"
          data-value={getAppUrl}
        />
        <Typography as="span" variant="body3" fontWeight={500} color={theme.colors.text.secondary}>
          Continue on
          <br />
          your phone
        </Typography>
      </StyledQrCard>
    </StyledRail>
  );
};
